// Original lessons for interpreting risk statistics and performance claims.
export const riskLessons = [
  {
    id: 'volatility-risk', moduleId: 'risk',
    title: '走勢很平穩，就代表沒有風險嗎？', subtitle: '報酬標準差衡量了什麼', minutes: 7, tag: '辨認風險',
    objectives: ['用偏離平均值的程度理解歷史波動率', '辨認波動率沒有涵蓋的損失與流動性風險'],
    paragraphs: [
      '常見的歷史波動率，是一段期間內報酬率的標準差，描述每期報酬偏離其平均值的程度。它不是價格最高減最低，也不宜直接用不同股價水準的價格標準差比較風險。計算前先確認使用日、月或年報酬、哪段樣本，以及有沒有年化。',
      '標準差會同時計入高於和低於平均的變動；它衡量起伏，不直接告訴你下一次下跌多少。兩個平均報酬一樣的樣本，過程可能很不同。樣本期間太短或只包含平靜市場，也可能沒遇到重要的壓力情境，過去低波動不能保證未來仍然平穩。',
      '風險還包括永久損失、無法順利出售、違約，以及需要用錢時剛好遇到下跌。成交稀少或估值更新慢的資產，畫面可能很平滑，卻不代表隨時能按顯示價格賣出。因此波動率應和資產性質、估值頻率及資金用途一起閱讀。',
    ],
    example: {
      title: '假設：相同平均月報酬，不同起伏',
      steps: ['甲三個月報酬為 −1%、1%、3%，乙為 −3%、1%、5%；兩者算術平均都是每月 1%。這三期僅供計算練習。', '甲相對平均的偏差為 −2、0、2 個百分點。採樣本標準差，平方後加總 8，除以 n−1 = 2，再開根號，得到 2% 的月波動率。', '乙的偏差為 −4、0、4 個百分點，同法得到 4% 的月波動率。這些是三個月樣本估計，並非年化，也不是未來每月最大跌幅。'],
      conclusion: '平均值相同，不表示經歷相同；波動較低也沒有替其他風險作保證。',
    },
    pitfalls: ['把年化波動率 15% 當成一年最多只會虧 15%。', '比較日波動與月波動卻不統一期間，或把少交易造成的平滑當成安全。'],
    glossary: [{ term: '樣本標準差', definition: '把各觀察值相對平均的偏差平方加總，除以樣本數減 1，再開根號，估計分散程度。' }, { term: '估值頻率', definition: '資產價值多久更新一次；更新慢可能掩蓋期間內真正的價格變動。' }],
    takeaway: '波動率描述一種歷史起伏，不能包辦所有投資風險。',
    exercise: '找一份基金月報的波動率，記下樣本期間、資料頻率與年化方式；再列出該資產兩種標準差未直接衡量的風險。',
    quiz: { question: '某商品過去一年公布的波動率很低，最合理的下一步是什麼？', options: ['認定未來不會出現大幅損失', '核對樣本與估值方式，再檢查流動性、信用等其他風險', '把波動率直接當成一年最大可能損失'], answer: 1, explanation: '波動率來自特定樣本與計算方式；低波動沒有涵蓋所有風險，也不是未來損失上限。' },
    sources: [
      { label: '市場先生｜歷史波動率與隱含波動率的主題閱讀', url: 'https://rich01.com/what-is-implied-volatility-0/', kind: 'reading' },
      { label: 'CFA Institute｜歷史報酬與標準差的衡量', url: 'https://rpc.cfainstitute.org/sites/default/files/-/media/documents/book/rf-publication/1989/rf-v1989-n3-sbbi-historical-returns.pdf', kind: 'official' },
      { label: 'SEC｜不同投資風險', url: 'https://www.investor.gov/introduction-investing/investing-basics/what-risk', kind: 'official' },
    ],
  },
  {
    id: 'correlation-risk', moduleId: 'risk',
    title: '相關係數為零，真的互不影響嗎？', subtitle: '線性關係、共同口徑與變動的相關性', minutes: 8, tag: '分散的限制',
    objectives: ['分辨線性相關與統計獨立', '在相同期間、頻率及衡量幣別下比較報酬關係'],
    paragraphs: [
      '本課的相關係數指 Pearson 線性相關係數，介於 −1 與 1。接近 1 表示報酬較傾向同方向線性變動，接近 −1 則傾向相反；0 只表示沒有線性相關，不等於兩個資產完全沒有關係，更不等於可以互相保證不會同跌。',
      '投資比較應先把兩組報酬對齊相同日期、資料頻率、期間、幣別與含息口徑，並注意不同市場收盤時間。把一邊美元報酬和另一邊台幣報酬混用，或用價格水準取代報酬，可能回答了原本沒打算問的問題。若其中一組報酬完全不變、標準差為零，相關係數也無法依一般公式定義。',
      '相關係數是一段樣本的統計，不是資產永久的標籤。換一段時間或遇到共同壓力，相關性可能改變。分散效果還取決於各資產波動、權重與尾部損失，不能只把兩個名稱不同、歷史相關較低的商品放一起，就宣稱整體風險已消失。',
    ],
    example: {
      title: '假設：零線性相關，仍能由甲推知乙',
      steps: ['設三種等可能情境，甲的報酬依序為 −10%、0%、10%，乙對應為 5%、−10%、5%；兩者平均都為 0%。', '用小數計算各情境的乘積，為 −0.005、0、0.005，平均為零；兩者各自標準差都非零，因此相關係數為 0。', '但只要知道甲的報酬是 0%，乙就一定是 −10%；甲不是 0% 時，乙一定是 5%。兩者仍有明確的非線性關係，並不獨立。'],
      conclusion: '這是刻意設計的數學模型，不是真實資產預測；它說明零相關所能支持的結論有限。',
    },
    pitfalls: ['看到相關係數 0，就解讀成完全獨立或絕不會同時虧損。', '拿不同年份、不同幣別的相關係數直接比較，卻沒有核對輸入資料。'],
    glossary: [{ term: '線性相關', definition: '兩個變數以直線關係共同變動的程度，不會完整描述所有非線性關係。' }, { term: '統計獨立', definition: '知道其中一個變數的結果，不會改變另一個結果的機率分布；比零相關更強。' }],
    takeaway: '相關係數要附上資料口徑；零只排除了線性關係，沒有排除所有共同風險。',
    exercise: '對兩項資產的同幣別月報酬，分開檢查前後兩段期間的相關係數；記錄樣本數，並說明數字改變不代表公式算錯。',
    quiz: { question: '兩項資產同期間報酬的相關係數為 0，能直接推出什麼？', options: ['兩者統計上一定獨立', '兩者不可能在同一天虧損', '該樣本沒有線性相關，仍可能有其他關係'], answer: 2, explanation: 'Pearson 相關係數衡量線性關係；零相關不保證獨立，也不排除同時下跌或尾部風險。' },
    sources: [
      { label: '市場先生｜相關係數的主題閱讀', url: 'https://rich01.com/blog-post_2-2/', kind: 'reading' },
      { label: 'Penn State｜相關係數與零相關不等於獨立', url: 'https://online.stat.psu.edu/stat414/Lesson18', kind: 'official' },
      { label: 'CFA Institute｜風險衡量中的相關與共同市場曝險', url: 'https://rpc.cfainstitute.org/sites/default/files/-/media/documents/code/gips/measures-risk-adjusted-return.pdf', kind: 'official' },
    ],
  },
  {
    id: 'beta-risk', moduleId: 'risk',
    title: 'Beta 是 1.2，不代表每天都是大盤的 1.2 倍', subtitle: '相對基準的歷史敏感度', minutes: 7, tag: '辨認基準',
    objectives: ['將 Beta 連回指定基準、樣本與回歸關係', '區分市場連動風險與全部資產波動'],
    paragraphs: [
      'Beta 描述資產報酬相對某個基準的線性敏感度，常由歷史同期報酬的回歸估計。你必須先知道基準是哪個指數、使用哪段期間及頻率；同一資產換了基準，Beta 可能不同。報酬或超額報酬的資料定義也要一致，不能只抄一個沒有標示來源的數字。',
      '常見公式是資產與基準報酬的共變異數除以基準變異數；在兩者標準差非零時，也可寫成「相關係數 × 資產標準差 ÷ 基準標準差」。這表示 Beta 同時受到共同變動與各自波動影響，不是單純把兩個波動率相除。',
      'Beta 為 1.2，是特定線性模型的斜率估計，不是每日報酬承諾。實際資產仍有截距與模型沒解釋的變動，估計也可能改變。低 Beta 只表示與選定基準的連動較低，不能排除公司事件、流動性或其他來源造成的大幅損失。',
    ],
    example: {
      title: '假設：共同變動只占一部分',
      steps: ['同一組月資料估得相關係數 0.8，資產月報酬標準差 18%，基準月報酬標準差 12%；數字只供公式練習。', 'Beta = 0.8 × 18% ÷ 12% = 1.2。若只用 18% ÷ 12%，會錯算成 1.5。', '若某月基準報酬 2%，模型中與基準相乘的那一項是 1.2 × 2% = 2.4%；仍須加上截距與未解釋變動，不能說資產當月必漲 2.4%。'],
      conclusion: 'Beta 先回答「相對哪個基準」，再回答歷史連動程度；它不等於產品的槓桿保證。',
    },
    pitfalls: ['把 Beta 和每日槓桿 ETF 的產品目標混為一談。', '認為 Beta 低就等於總波動小，漏掉不隨基準變動的風險。'],
    glossary: [{ term: '基準（Benchmark）', definition: '用來比較資產表現或估計連動性的參考報酬系列，選擇不同會影響結果。' }, { term: '回歸斜率', definition: '線性模型中，基準變動一個單位所對應的資產變動估計，不包含所有個別結果。' }],
    takeaway: 'Beta 不是每天的倍率，要連同基準、樣本與未解釋風險一起看。',
    exercise: '找一個網站公布的 Beta，查出基準、回溯期間及資料頻率；若查不到，先把數字標成定義不足，不將它與另一網站直接排名。',
    quiz: { question: '相同口徑下，相關係數 0.5、資產標準差 20%、基準標準差 10%，Beta 為多少？', options: ['1.0', '2.0', '0.5'], answer: 0, explanation: '0.5 × 20% ÷ 10% = 1.0。資產總波動是基準的兩倍，但與基準的線性連動較低，因此 Beta 不等於 2。' },
    sources: [
      { label: '市場先生｜Alpha 與 Beta 的主題閱讀', url: 'https://rich01.com/what-is-alpha-beta-with-risk/', kind: 'reading' },
      { label: 'CFA Institute｜Beta 計算及其限制', url: 'https://rpc.cfainstitute.org/sites/default/files/-/media/documents/code/gips/measures-risk-adjusted-return.pdf', kind: 'official' },
    ],
  },
  {
    id: 'sharpe-ratio', moduleId: 'risk',
    title: '夏普值比較高，就一定比較安全？', subtitle: '超額報酬、波動與負值陷阱', minutes: 8, tag: '檢視績效',
    objectives: ['用一致期間與口徑計算超額報酬相對波動', '識別負分子、零分母與單一指標排名的限制'],
    paragraphs: [
      '本課的 Sharpe ratio（夏普值）先逐期用投資報酬減去同幣別、同期間的無風險報酬，再以這組超額報酬的算術平均除以其標準差。若每期無風險報酬固定，分母也等於投資報酬的標準差；若不固定，不能直接忽略差別。',
      '分子與分母必須使用一致頻率及樣本。不能用年化 CAGR 減年利率，卻除以月波動，當成相同定義的夏普值；年化方法也有假設，需要核對。若標準差為零，這個除法沒有定義，不能顯示成「零風險高分」或直接給零分。',
      '當平均超額報酬為負，同樣負分子除以更大的波動，數字會更接近零、看起來更高，卻不表示更安全。即使為正，高夏普也可能受樣本、估值平滑或少數未出現的尾部事件影響。比較前要一起看金額損益、風險來源、費用口徑與資料長度。',
    ],
    example: {
      title: '假設：先算正值，再檢查負值比較',
      steps: ['一組年度資料的算術平均報酬為 7%，每年無風險報酬固定 1%，超額報酬標準差為 12%，年度夏普值 =（7% − 1%）÷ 12% = 0.5。', '另一組獨立比較中，甲乙平均年報酬都為 1%，無風險報酬固定 3%，甲的標準差 5%、乙 10%。', '甲夏普值 = −2% ÷ 5% = −0.4；乙 = −2% ÷ 10% = −0.2。乙數值較高，但在這個設定中波動更大，平均超額報酬也沒有改善。'],
      conclusion: '夏普值描述特定樣本的比例；不能只靠大小，尤其不能用負值排名宣稱安全程度。',
    },
    pitfalls: ['把夏普 1 解讀成保證賺 1%，或宣稱未來沒有虧損風險。', '為了得到漂亮數字，混用 CAGR、不同期間無風險利率與不同頻率標準差。'],
    glossary: [{ term: '超額報酬', definition: '每期投資報酬減去本課指定的同期間無風險報酬，可能為正或負。' }, { term: '算術平均報酬', definition: '將各期報酬率相加除以期數，與描述累積成長速度的 CAGR 不同。' }],
    takeaway: '先查夏普值怎麼算，再問這個比例漏掉哪些風險。',
    exercise: '用平均報酬 0%、固定無風險報酬 2%，分別代入 4% 與 8% 標準差，觀察數字排名；再說明為何不能據此認定 8% 波動的方案較安全。',
    quiz: { question: '超額報酬序列的標準差為零時，依本課公式，夏普值應如何處理？', options: ['直接顯示為 0', '標記無法定義，不能當成一般有效分數', '自動顯示為滿分並認定安全'], answer: 1, explanation: '分母為零，平均超額報酬除以標準差沒有一般有效數值；即使觀察到報酬平滑，也不能直接推論不存在其他風險。' },
    sources: [
      { label: '市場先生｜夏普率的主題閱讀', url: 'https://rich01.com/what-sharpe-ratio/', kind: 'reading' },
      { label: 'William F. Sharpe｜夏普值原文與計算口徑', url: 'https://web.stanford.edu/~wfsharpe/art/sr/sr.htm', kind: 'official' },
    ],
  },
  {
    id: 'tail-risk', moduleId: 'risk',
    title: 'VaR 寫一萬二，最多就只會虧一萬二？', subtitle: '損失門檻、尾部與模型限制', minutes: 8, tag: '極端情境',
    objectives: ['連同期間、信心水準及模型解讀 VaR', '區分分位數門檻與尾部損失嚴重程度'],
    paragraphs: [
      '風險值 VaR 用損失分布中的特定百分位，摘要某個持有期間的損失門檻。例如模型算出一天 95% VaR 為 12,000 元，是在該模型與條件下描述 95% 分位的位置，不是保證損失最多 12,000 元。期間、信心水準、持倉與計算方法都不能省略。',
      '超過門檻的那一小段分布稱為尾部，損失可能大得多。預期短缺（Expected shortfall，ES）進一步關心最差那部分情境的平均損失，和 VaR 的門檻是不同問題。不過 ES 也依賴模型與資料，仍不是任何可能世界的損失上限。',
      '若假設分布、資產關係或流動性在壓力時失效，實際超過 VaR 的頻率與金額可能和估計不同。95% 不表示每二十天固定出現一次超標，也不表示接下來十九天安全。解讀廣告時應追問估計依據、壓力情境，以及歷史樣本沒出現過的風險。',
    ],
    example: {
      title: '假設：100 個等可能的一天損失情境',
      steps: ['把損失由小排到大，假設前 95 個不超過 12,000 元，而且第 95 個恰為 12,000 元；用本例的分位數約定，一天 95% VaR 為 12,000 元。', '最差五個情境分別損失 15,000、18,000、25,000、50,000、90,000 元，全部超過 VaR。', '這個離散例子中，最差 5% 的平均損失 =（15,000 + 18,000 + 25,000 + 50,000 + 90,000）÷ 5 = 39,600 元，和門檻 12,000 元不同。'],
      conclusion: '連例子裡最大損失 90,000 元，都只是已設定情境的最大值，沒有替真實未來設定上限。',
    },
    pitfalls: ['把 VaR 翻成最大可能損失，忽略門檻外的情境。', '直接比較一天 95% VaR 和十天 99% VaR，沒有統一期間、信心水準與持倉。'],
    glossary: [{ term: '尾部風險', definition: '損失分布較極端的一端所帶來的風險，可能在短歷史樣本中很少出現。' }, { term: '預期短缺（ES）', definition: '關注指定尾部機率內的平均損失；離散資料的分位數邊界需要說明計算約定。' }],
    takeaway: 'VaR 是有條件的損失分位數，門檻之外的傷害仍要另外問。',
    exercise: '看到一個 VaR 數字時，寫下持有期間、信心水準、金額或百分比、持倉日期與模型；再列一個原樣本沒有涵蓋的壓力情境。',
    quiz: { question: '某模型估計一天 99% VaR 為 20,000 元，可以直接宣稱哪一項？', options: ['一天最多只能損失 20,000 元', '接下來 99 天都一定安全', '它是該模型與期間的損失分位數，仍可能損失更多'], answer: 2, explanation: 'VaR 沒有說明門檻外損失的上限，也不是未來逐日安全保證；實際結果還取決於模型是否適用。' },
    sources: [
      { label: '市場先生｜歷史經驗與厚尾風險的主題閱讀', url: 'https://rich01.com/blog-post_39/', kind: 'reading' },
      { label: 'BIS｜VaR 與 Expected Shortfall 的風險術語', url: 'https://www.bis.org/committees/bcbs/basel-framework/standard/mar/10/inforce/2022-01-01/published/2019-12-15', kind: 'official' },
    ],
  },
  {
    id: 'backtest-bias', moduleId: 'risk',
    title: '回測曲線很漂亮，當時真的做得到嗎？', subtitle: '倖存者、前視與過度擬合偏誤', minutes: 8, tag: '審查廣告',
    objectives: ['檢查回測是否保留失敗標的與當時可知資訊', '區分歷史調參成果、樣本外驗證與實際可執行性'],
    paragraphs: [
      '回測是在歷史資料上模擬一套規則，答案受選擇的資料與假設影響。倖存者偏誤會把已下市、合併或停止運作的失敗標的排除；前視偏誤則讓模擬使用決策時尚未公布的財報、修正版數據或未來成分股名單。這些做法會讓歷史能力看起來比當時真正可做到的更好。',
      '過度擬合是反覆調整參數或挑選規則，直到剛好貼合已看過的資料。從幾百個方案只展示最好的那個，容易把偶然誤認為方法有效。應保留試過的版本，把設計與驗證期間分開；但若看完樣本外結果又反覆調整，該區間也逐漸成為開發資料。',
      '即使規則固定，還要檢查成交時點、買賣價差、手續費、滑價、可交易數量與流動性。收盤後才知道的訊號，不能無條件假設已在同一收盤價成交。樣本外、不同時期與壓力測試能增加證據，仍不能保證未來；績效廣告應清楚區分回測、模擬與真實交易紀錄。',
    ],
    example: {
      title: '假設：把倒閉的標的刪掉，結果如何改變？',
      steps: ['期初三項投資各放 10,000 元，期間總報酬分別 +30%、+10%、−100%，沒有中途現金流或調倉，先忽略費用。', '完整組合期末為 13,000 + 11,000 + 0 = 24,000 元；相對原始 30,000 元，報酬為 −20%。', '若回測只留下仍存續的前兩項，會展示（30% + 10%）÷ 2 = +20%，把原本的虧損樣本改成了獲利樣本。', '回到完整組合，若另假設期末須支付尚未計入的總成本 300 元，淨值為 23,700 元，報酬 −21%；成本也不能因資料難取得就當成不存在。'],
      conclusion: '曲線漂亮之前，先確認它留下了誰、何時才知道資訊，以及每筆交易是否做得到。',
    },
    pitfalls: ['用今天還活著的基金或公司名單回推多年績效，排除中途消失的標的。', '一再根據樣本外結果改參數，卻繼續稱它是完全未看過的獨立驗證。'],
    glossary: [{ term: '前視偏誤', definition: '模擬決策使用了當時尚不可取得的資訊，讓回測擁有不真實的先知能力。' }, { term: '過度擬合', definition: '規則過度迎合已觀察樣本的細節或雜訊，未必能適用其他資料或未來。' }],
    takeaway: '回測先審查資料、時間線與交易假設，再看報酬排名。',
    exercise: '挑一則績效廣告，檢查它是否交代資料名單如何取得、訊號何時可見、成本與成交假設、試過多少版本，以及哪段資料從未用來調整規則。',
    quiz: { question: '用 3 月 20 日才公布的年度財報，決定回測中 2 月 1 日要買哪些股票，主要是哪種問題？', options: ['前視偏誤：用到了決策時尚未公開的資訊', '只要報酬很好就沒有問題', '單純是手續費設定不同'], answer: 0, explanation: '財報所屬年度已結束，不等於資料已公開。2 月 1 日的決策不能無條件使用 3 月 20 日才公布的資訊。' },
    sources: [
      { label: '市場先生｜回測的限制與過度最佳化', url: 'https://rich01.com/limitation-of-backtesting/', kind: 'reading' },
      { label: 'CFA Institute｜回測、模擬與資料偏誤', url: 'https://www.cfainstitute.org/insights/professional-learning/refresher-readings/2026/backtesting-and-simulation', kind: 'official' },
      { label: 'CFA Institute｜投資模型驗證指南', url: 'https://rpc.cfainstitute.org/-/media/documents/article/rf-brief/investment-model-validation.pdf', kind: 'official' },
    ],
  },
];
